import React, { useEffect, useRef, useState } from "react";
import { useHistory } from 'react-router-dom';
import { useParams } from "react-router";
import { useTranslation } from "react-i18next";
import classnames from "classnames";

import Loading from "../../../components/Loading";
import { CreatableCustomSelect } from "../../Dish/CreatableCustomSelect";
import Tags from "./Tags";
import Allergens from "./Allergens";
import Tax from "../../Dish/Tax";
import { FormField } from "../../../components/FormField";
import { Input } from "../../../components/Input";
import Button from "../../../components/Button/Button";
import { useGetSettings } from '../../../network';
import { CustomSelect } from '../../../components/Shared/CustomSelect';

const PHOTO_SUPPORTED_FORMATS = ["image/jpg", "image/jpeg", "image/png"];
const PHOTO_MAX_SIZE = 2;

export const Dish = ({
    dish = {},
    loading,
    tax,
    defaultLanguage,
    allergens: allergenOptions,
    editDish,
    formik,
    dishOptions,
    restaurants
}) => {
    const { t } = useTranslation();
    const { catId, dishId } = useParams();

    const photoRef = useRef(null);
    const cn = classnames;

    const { data: settings = {} } = useGetSettings();

    const {
        categories,
        tags: tagsOptions = [],
    } = dishOptions;


    const [preview, setPreview] = useState("");
    const [invalidPhoto, setValidPhoto] = useState("");
    const { price, calories, weight, tags, allergens, tax_percent } =
        formik.values;
    const { handleChange, errors } = formik;

    let total = price ? Number(price) : 0;

    if (tax_percent) {
        const vat = total / (100 + tax_percent) * tax_percent
        total = (total - vat).toFixed(2);
    }

    const isAllDisabled = !!dish.archived_at || loading;


    const handleUpdateStatus = () => {
        const { photo, ...dish } = formik.values;
        const archived = dish.archived_at ? "0" : "1";
        editDish({ dish, photo, archived });
    };

    const onClickAddPhoto = () => photoRef.current.click();

    const onChangePhoto = (e) => {
        let reader = new FileReader();
        let file = e.target.files[0];
        const isValidFormat = PHOTO_SUPPORTED_FORMATS.includes(file.type);
        const isValidSize = (file.size / 1024 / 1024) <= PHOTO_MAX_SIZE

        if (!isValidFormat) {
            setValidPhoto(t("Image format JPEG/PNG"));
            return;
        }

        if (isValidFormat && !isValidSize) {
            setValidPhoto(`${t("Max file size is")} ${PHOTO_MAX_SIZE} MB`);
            return;
        }

        setValidPhoto("");
        reader.onloadend = () => {
            setPreview(reader.result);
        };
        reader.readAsDataURL(file);

        formik.setFieldValue("photo", e.currentTarget.files[0]);
    };


    const onChangeInfo = (e) => {
        formik.setFieldValue(formik.values.language, { ...(formik.values[formik.values.language] || {}), [e.target.name]: e.target.value });
    };

    useEffect(() => {
        if (dishId && (dish.title || dish.name)) {
            setPreview(dish.photo);
        }
    }, [dish]);

    return (
        <Loading loading={loading}>
            <div className="grid grid-cols-2 gap-2">
                <div className="pt-5 pr-16 pb-4 pl-5">
                    <div className="col-span-3 sm:col-span-3 py-1 mb-1">
                        <FormField
                            label={t("Category")}
                            htmlFor="categories"
                            required
                            error={errors.category_id}
                            disabled={isAllDisabled}
                            popoverText={t("dish is archived")}
                        >
                            <CreatableCustomSelect
                                inputId="categories"
                                formik={formik}
                                categoryId={dish.category_id}
                                isDisabled={isAllDisabled}
                                categories={categories}
                            />
                        </FormField>
                    </div>
                    <div className="col-span-3 sm:col-span-3 py-1 mb-1">
                        <FormField
                            label={t("Name")}
                            htmlFor="title"
                            required
                            error={errors.title}
                            disabled={isAllDisabled}
                            popoverText={t("dish is archived")}
                        >
                            <Input
                                id="title"
                                name="title"
                                value={formik.values[defaultLanguage.value]?.title}
                                disabled={isAllDisabled}
                                placeholder={t("Name")}
                                onChange={onChangeInfo}
                            />
                        </FormField>
                    </div>
                    <div className="col-span-3 sm:col-span-3 py-1 mb-1">
                        <div className="mt-1">
                            <FormField
                                label={t("Description")}
                                htmlFor="description"
                                error={errors.description}
                                disabled={isAllDisabled}
                                popoverText={t("dish is archived")}
                            >
                                <textarea
                                    id="description"
                                    name="description"
                                    rows="4"
                                    placeholder={t("Description")}
                                    value={formik.values[defaultLanguage.value]?.description}
                                    onChange={onChangeInfo}
                                    disabled={isAllDisabled}
                                    className="input mt-1 border-none focus:ring-gray-200  focus:border-gray-100 bg-gray-50 block w-full shadow-sm sm:text-sm rounded-md"
                                />
                            </FormField>
                        </div>
                    </div>
                    <div className="col-span-3 sm:col-span-3 py-1 mb-1 grid grid-cols-3 items-start gap-2">
                        <div className="">
                            <FormField
                                label={t("Price")}
                                htmlFor="price"
                                required
                                error={errors.price}
                                disabled={isAllDisabled}
                                popoverText={t("dish is archived")}
                            >
                                <div className="mt-1 relative">
                                    <span className="absolute inset-y-0 left-0 flex items-center pl-2 border-r border-gray-200 pr-3">
                                        {settings.currency?.title}
                                    </span>
                                    <input
                                        id="price"
                                        name="price"
                                        placeholder={t("Price")}
                                        value={price}
                                        onChange={handleChange}
                                        disabled={isAllDisabled}
                                        className="h-10 border-gray-200 focus:border-gray-100 bg-gray-50
                                                        focus:ring-gray-200 rounded-md shadow-sm mt-1 block w-full pl-12"
                                        type="text"
                                    />
                                </div>
                            </FormField>
                        </div>
                        <div>
                            <FormField className="mt-1" label={t("VAT")} htmlFor="VAT"
                                       disabled={isAllDisabled}
                                       popoverText={t("dish is archived")}
                            >
                                <Tax
                                    tax={tax}
                                    dishTax={formik.values.tax_percent}
                                    isAllDisabled={isAllDisabled}
                                    formik={formik}
                                />
                            </FormField>
                        </div>
                        <div>
                            <FormField
                                label={t("Price excluding VAT")}
                                htmlFor="total_price"
                                className="whitespace-nowrap"
                                error={errors.total_price}
                                disabled={isAllDisabled}
                                popoverText={t("dish is archived")}
                            >
                                <div className="mt-1 relative">
                                    <span className="absolute inset-y-0 left-0 flex items-center pl-2 border-r border-gray-200 pr-3">
                                        {settings.currency?.title}
                                    </span>
                                    <input
                                        id="total_price"
                                        name="total_price"
                                        value={total}
                                        onChange={handleChange}
                                        disabled={isAllDisabled}
                                        className="border-gray-200 focus:border-gray-100 bg-gray-100
                                                        focus:ring-gray-200 rounded-md shadow-sm mt-1 block w-full pl-12"
                                        type="text"
                                        readOnly
                                    />
                                </div>
                            </FormField>
                        </div>
                    </div>
                    <div className="col-span-3 sm:col-span-3 py-1 mb-1 mt-3 block w-full bg-white rounded-md focus:outline-none sm:text-sm">
                        <FormField
                            label={t("Selected Stores")}
                            htmlFor="restaurants"
                            error={errors.restaurants}
                            disabled={true}
                            popoverText={t("You can't select store from dish page")}
                        >
                                <CustomSelect
                                    placeholder={t("Stores")}
                                    isMulti
                                    isSearchable={false}
                                    isClearable={false}
                                    clearable={false}
                                    isDisabled={true}
                                    className="basic-multi-select mt-2"
                                    components={{ DropdownIndicator:() => null, IndicatorSeparator:() => null }}                                    classNamePrefix="select"
                                    config={{multiValueRemove: (base) => ({ ...base, display: 'none' })}}
                                    getOptionLabel={(opt) => opt.name}
                                    getOptionValue={(opt) => opt.id}
                                    value={restaurants}
                                />
                        </FormField>
                    </div>
                </div>
                <div className="pt-5 pr-5 pb-4">
                    <div className="col-span-3 sm:col-span-3 py-1 mb-1 px-3">
                        <div
                            x-data="{photoName: null, photoPreview: null}"
                            className="col-span-6 sm:col-span-4"
                        >
                            <input
                                name="photo"
                                id="photo"
                                type="file"
                                className="hidden"
                                disabled={isAllDisabled}
                                ref={photoRef}
                                onChange={(e) => onChangePhoto(e)}
                            />
                            <label
                                htmlFor="photo_file"
                                className="block font-bold text-black-800"
                            >
                                {t("Upload Image")}
                            </label>
                            {!preview ? (
                                <>
                                    <div className="mt-2 flex items-end">
                                        <div
                                            style={{
                                                background:
                                                    "rgba(52, 64, 84, 0.5)",
                                                display: "flex",
                                                justifyContent: "center",
                                                alignItems: "center",
                                                width: "150px",
                                                height: "150px",
                                            }}
                                            className="rounded object-cover"
                                        >
                                            <span className="text-white text-lg font-bold">
                                                280 x 280px
                                            </span>
                                        </div>
                                        <button
                                            type="button"
                                            disabled={isAllDisabled}
                                            className="inline-flex justify-center mt-2 ml-5 mr-2 py-1 px-3 border border-transparent shadow-sm text-sm font-medium rounded-lg text-white bg-purple-500 hover:bg-purple-300 activ:bg-gray-700 focus:outline-none"
                                            onClick={onClickAddPhoto}
                                        >
                                            {t("Upload New Image")}
                                        </button>
                                    </div>
                                    {invalidPhoto && (
                                        <div className="text-red-500 text-sm mt-1">
                                            {invalidPhoto}
                                        </div>
                                    )}
                                </>
                            ) : null}
                            {preview ? (
                                <>
                                    <div className="mt-2 flex items-end">
                                        <span
                                            className="rounded w-40 h-40"
                                            style={{
                                                backgroundSize: "cover",
                                                backgroundRepeat: "no-repeat",
                                                backgroundPosition:
                                                    "center center",
                                                backgroundImage: `url(${preview})`,
                                            }}
                                        ></span>
                                        <button
                                            type="button"
                                            disabled={isAllDisabled}
                                            className="inline-flex justify-center mt-2 ml-5 mr-2 py-1 px-3 border border-transparent shadow-sm text-sm font-medium rounded-lg text-white bg-purple-500 hover:bg-purple-300 activ:bg-gray-700 focus:outline-none"
                                            onClick={onClickAddPhoto}
                                        >
                                            {t("Upload New Image")}
                                        </button>
                                    </div>
                                    {invalidPhoto && (
                                        <div className="text-red-500 text-sm mt-1">
                                            {invalidPhoto}
                                        </div>
                                    )}
                                </>
                            ) : null}
                        </div>
                    </div>
                    <div className="col-span-3 sm:col-span-3 py-1 mb-1 block w-full px-3 bg-white rounded-md focus:outline-none sm:text-sm">
                        <FormField
                            label={t("Allergens")}
                            htmlFor="allergens"
                            error={errors.allergens}
                            disabled={isAllDisabled}
                            popoverText={t("dish is archived")}
                        >
                            <Allergens
                                options={allergens}
                                allergens={allergenOptions}
                                formik={formik}
                                isAllDisabled={isAllDisabled}
                            />
                        </FormField>
                    </div>
                    <div className="col-span-3 sm:col-span-3 py-1 mb-1 flex justify-between px-3 gap-4">
                        <div className="w-1/2">
                            <FormField
                                label={t("Weight (g)")}
                                htmlFor="weight"
                                error={errors.weight}
                                disabled={isAllDisabled}
                                popoverText={t("dish is archived")}
                            >
                                <input
                                    name="weight"
                                    value={weight}
                                    placeholder={t("(Grams)")}
                                    onChange={handleChange}
                                    disabled={isAllDisabled}
                                    className="input mt-1 border-none focus:ring-gray-200  focus:border-gray-100 bg-gray-50 block w-full sm:text-sm rounded-md"
                                    type="number"
                                />
                            </FormField>
                        </div>
                        <div className="w-1/2">
                            <FormField
                                label={t("Calories")}
                                htmlFor="calories"
                                error={errors.calories}
                                disabled={isAllDisabled}
                                popoverText={t("dish is archived")}
                            >
                                <input
                                    name="calories"
                                    value={calories}
                                    placeholder={t("(Kcal)")}
                                    onChange={handleChange}
                                    disabled={isAllDisabled}
                                    className="input mt-1 border-none focus:ring-gray-200  focus:border-gray-100 bg-gray-50 block w-full sm:text-sm rounded-md"
                                    type="number"
                                />
                            </FormField>
                        </div>
                    </div>
                    <div className="col-start-1 col-end-5 block w-full py-2 px-3 bg-white rounded-md focus:outline-none sm:text-sm">
                        <FormField
                            label={t("Assign Food or Drink Tags")}
                            htmlFor="tags"
                            error={errors.tags}
                            disabled={isAllDisabled}
                            popoverText={t("dish is archived")}
                        >
                            <Tags
                                isAllDisabled={isAllDisabled}
                                options={tagsOptions}
                                tags={tags}
                                formik={formik}
                            />
                        </FormField>
                    </div>
                </div>
            </div>

            <div
                className={cn(
                    "px-4 py-5 text-right sm:px-6 flex items-center w-full",
                    {
                        "justify-between": dishId,
                        "justify-end": !dishId,
                    }
                )}
            >
                {dishId && (
                    <Button
                        type="button"
                        color="bg-danger"
                        className={cn("h-9", {
                            ["bg-purple-500 focus:border-purple-600 text-sm text-white shadow-sm hover:bg-purple-600"]:
                                dish.archived_at,
                        })}
                        isUpperCase={false}
                        disabled={loading}
                        onClick={handleUpdateStatus}
                    >
                        {dish.archived_at ? t("Activate") : t("Archive")}
                    </Button>
                )}

                <div>

                    <Button
                        type="link"
                        color="bg-gray-300"
                        isUpperCase={false}
                        to={`/chain/menu/${catId}/items`}
                        disabled={isAllDisabled}
                        className="h-9 inline-flex justify-center py-1.5 px-5 border border-transparent shadow-sm text-sm font-bold rounded-lg bg-gray-100 hover:bg-gray-200 focus:outline-none"
                    >
                        {t("Cancel")}
                    </Button>
                    <Button
                        type="submit"
                        isUpperCase={false}
                        isLoading={loading}
                        disabled={isAllDisabled}
                        className="h-9 inline-flex justify-center py-1.5 px-6 ml-2 border border-transparent shadow-sm text-sm font-medium rounded-lg text-white bg-gray-900 hover:bg-gray-700 focus:outline-none"
                    >
                        {t("Save")}
                    </Button>
                </div>
            </div>
        </Loading>
    );
};
